import {
  AllAttributesType,
  allAttrsEnum,
  CHART_DISPLAY_MAX_ITEMS,
  MetricsType,
  noPercentageValuesAttributes,
  NullableLineChartTimeGroupingType,
  onlyPercentageValuesAttributes,
  TABLE_DISPLAY_PER_PAGE,
  WidgetDisplayOptions,
} from '@/services/api/resources/analytics-builder/constants';
import {
  FormItemFilterRecord,
  ReportFilter,
  ReportWidget,
} from '@/services/api/resources/analytics-builder/model';
import { all as getData, allPublic as getPublicData } from '@/services/api/resources/analytics/actions';
import { computed, ref } from 'vue';
import { useTask } from '@/ui/concerns';
import { ChartDataType, parseWidgetData } from '@/services/api/resources/analytics-builder/transform';
import * as TE from 'fp-ts/TaskEither';
import { retrieveFormItem } from '@/services/analytics/analyticsService';
import { useFormItemsStore } from '@/stores/analytics/formItems';
import { ResponsesTypes } from '@/entities/checklist';
import { imageUrl, fileUrlWithoutToken } from '@/services/assets-api/assetsClient';
import mergeImages from 'merge-images';
import { v4 as uuidv4 } from 'uuid';
import { ApiError } from '@/services/api';
import { addExtensionToBase64File } from '@/ui/helpers/fileHelper';
import FileSaver from 'file-saver';
import HtmlHelper from '@/ui/helpers/HtmlHelper';
import { compactImagesFromDataUrl } from '@/services/file/zipProcessor';
import { useDisplay } from '@/ui/concerns/analytics/widget/useDisplay';

export interface WidgetRequestInput {
  filters: ReportFilter;
  metric: MetricsType | null;
  attribute: AllAttributesType | null;
  lineChartTimeGrouping: NullableLineChartTimeGroupingType;
  display: WidgetDisplayOptions | null;
  percentageToggled: boolean;
  page: number | undefined;
  perPage: number | undefined;
}

export const useWidgets = () => {
  const combineFilters = (widgetFilter: ReportFilter, reportFilter: ReportFilter): ReportFilter => ({
    ...widgetFilter,
    date: widgetFilter.date.range !== null ? widgetFilter.date : reportFilter.date,
    folder: widgetFilter.folder.values.length > 0 ? widgetFilter.folder : reportFilter.folder,
    entity: widgetFilter.entity.values.length > 0 ? widgetFilter.entity : reportFilter.entity,
    group: widgetFilter.group.values.length > 0 ? widgetFilter.group : reportFilter.group,
    user: widgetFilter.user.values.length > 0 ? widgetFilter.user : reportFilter.user,
    category: widgetFilter.category.values.length > 0 ? widgetFilter.category : reportFilter.category,
    checklist: widgetFilter.checklist.values.length > 0 ? widgetFilter.checklist : reportFilter.checklist,
    punctuality: reportFilter.punctuality ?? widgetFilter.punctuality,
  });

  const getPage = (page: number, selectedDisplay: WidgetDisplayOptions | null) => {
    switch (true) {
      case selectedDisplay === WidgetDisplayOptions.singleNumber:
        return undefined;
      case selectedDisplay === WidgetDisplayOptions.table:
        return page;
      default:
        return 1;
    }
  };

  const getPerPage = (selectedDisplay: WidgetDisplayOptions | null) => {
    switch (true) {
      case selectedDisplay === WidgetDisplayOptions.singleNumber:
        return undefined;
      case selectedDisplay === WidgetDisplayOptions.table:
        return TABLE_DISPLAY_PER_PAGE;
      default:
        return CHART_DISPLAY_MAX_ITEMS;
    }
  };

  const shouldTogglePercentageOnAttributeChange = (attribute: AllAttributesType | null, percentage: boolean) =>
    (noPercentageValuesAttributes.includes(attribute as AllAttributesType) && percentage)
    || (onlyPercentageValuesAttributes.includes(attribute as AllAttributesType) && !percentage);

  const shouldChangeTimeGroupingOnAttributeChange = (
    attribute: AllAttributesType | null,
    timeGrouping: NullableLineChartTimeGroupingType,
  ) => attribute === allAttrsEnum.year && timeGrouping === 'year';

  return {
    combineFilters,
    getPage,
    getPerPage,
    shouldTogglePercentageOnAttributeChange,
    shouldChangeTimeGroupingOnAttributeChange,
  };
};

export const useWidgetRequest = (reportId: number | null) => {
  const store = useFormItemsStore();
  const formItemDetails = ref<FormItemFilterRecord>();
  const data = ref();

  const makeRequest = (input: WidgetRequestInput) => {
    const { isChart } = useDisplay();
    let payload = {
      ...input,
      page: isChart(input.display!) ? undefined : input.page,
      perPage: isChart(input.display!) ? undefined : input.perPage,
    };

    if (payload.filters.formItem) {
      const detail = store.find(payload.filters.formItem);

      if (detail?.checklistId) {
        payload = {
          ...payload,
          filters: {
            ...payload.filters,
            checklist: {
              values: [detail.checklistId],
            },
          },
        };
      }
    }

    return reportId ? getPublicData(payload, reportId) : getData(payload);
  };

  const getWidgetData = useTask(
    (input: WidgetRequestInput | undefined) => makeRequest(input!),
    (response, input) => {
      data.value = parseWidgetData(
        response.parsed as ChartDataType,
        input!.attribute,
        input!.metric,
        input!.lineChartTimeGrouping,
        input!.display,
        input!.percentageToggled,
      ) as Array<unknown>;
    },
    uuidv4(),
    (error: ApiError) => console.error(error),
  );

  const getFormItemDetails = useTask(
    (input: { id: string | number | undefined; cb: () => void } | undefined) => {
      if (!input?.id) {
        return TE.right({ data: [] });
      }

      return retrieveFormItem(String(input.id), reportId);
    },
    (formItem, input) => {
      if (formItem.data.length > 0) {
        const [item] = formItem.data;
        formItemDetails.value = item;
        store.add(item);
      }

      if (input) {
        input.cb();
      }
    },
  );

  const getWidgetDataTask = computed(() => getWidgetData);
  const getFormItemDetailsTask = computed(() => getFormItemDetails);

  return {
    getWidgetDataTask,
    getFormItemDetailsTask,
    data,
    formItemDetails,
  };
};

export const useWidgetFormItem = (widget: ReportWidget) => {
  const store = useFormItemsStore();
  const isDownloadingImage = ref(false);

  const getDetails = () => store.formItems.get(widget.filters.formItem as string | number);
  const mergeImagesAnonymously = async (images: string[]) => mergeImages(images, { crossOrigin: 'anonymous' });
  const mapImageIdsToUrls = ([image, drawing]: string[]) => drawing
    ? [imageUrl(image), imageUrl(drawing)] : [imageUrl(image)];
  const mapFileIdsToUrls = ([id, name]: string[]) => ({ id: fileUrlWithoutToken(id), name });

  const isResponseAttributeSelected = computed(() => widget.attribute === allAttrsEnum.response);

  const isDownloadable = computed(() => {
    const details = getDetails();

    return details
      ? [
        ResponsesTypes.photo,
        ResponsesTypes.signature,
        ResponsesTypes.annotation,
        ResponsesTypes.file,
      ].includes(details.type as ResponsesTypes)
      : false;
  });

  const canHaveMultipleAssets = computed(() => {
    const details = getDetails();
    return details ? [ResponsesTypes.photo, ResponsesTypes.file].includes(details.type as ResponsesTypes) : false;
  });

  const isFileAsset = computed(() => getDetails()?.type === ResponsesTypes.file);

  const downloadAsset = async (json: string) => {
    const details = getDetails();
    details?.type === ResponsesTypes.file
      ? await downloadFile(json)
      : await downloadImage(json);
  };

  const downloadImage = async (json: string) => {
    isDownloadingImage.value = true;
    try {
      const imagesArray = JSON.parse(json);

      switch (true) {
        case !Array.isArray(imagesArray):
          console.error('Invalid image response data');
          return;
        case imagesArray.length === 0:
          console.error('No images to download');
          return;
        default:
          break;
      }

      if (imagesArray.length > 1) {
        const urls = imagesArray.map(mapImageIdsToUrls);
        const promises = urls.map((images: string[]) => mergeImagesAnonymously(images));
        const dataUrls = await Promise.all(promises);

        compactImagesFromDataUrl(dataUrls).then(content => FileSaver.saveAs(content, 'images.zip'));
        return;
      }

      const url = await mergeImagesAnonymously(mapImageIdsToUrls(imagesArray[0]));

      new HtmlHelper()
        .getLinkElementToDownload(url, addExtensionToBase64File(url, getDetails()?.title || 'image'))
        .click();
    } catch (e) {
      console.error('Error downloading image');
    } finally {
      isDownloadingImage.value = false;
    }
  };

  const downloadFile = async (json: string) => {
    isDownloadingImage.value = true;
    try {
      const filesArray = JSON.parse(json);

      switch (true) {
        case !Array.isArray(filesArray):
          console.error('Invalid file response data');
          return;
        case filesArray.length === 0:
          console.error('No files to download');
          return;
        default:
          break;
      }

      if (filesArray.length > 1) {
        return;
      }

      const url = mapFileIdsToUrls(filesArray[0]);
      const link = new HtmlHelper().getLinkElementToDownload(url.id, url.name);
      link.setAttribute('target', '_blank');
      link.click();
    } catch (e) {
      console.error('Error downloading file');
    } finally {
      isDownloadingImage.value = false;
    }
  };

  const getFiles = (json: string) => {
    try {
      const filesArray = JSON.parse(json);
      return filesArray.map((file: string[]) => mapFileIdsToUrls(file));
    } catch (e) {
      console.error('Error getting files');
      return [];
    }
  };

  const countAssets = (json: string) => JSON.parse(json)?.length;

  return {
    isResponseAttributeSelected,
    isDownloadable,
    canHaveMultipleAssets,
    isFileAsset,
    isDownloadingImage,
    downloadAsset,
    countAssets,
    getFiles,
  };
};
